<template>
  <PVButtonGroup
    ref="buttonRef"
    :data-testid="dataTestid"
    :disabled="isButtonBusyOrDisabled"
    :pt="passThroughProps"
    unstyled
  >
    <slot />
  </PVButtonGroup>
</template>

<script lang="ts">
import { PassThrough } from '@primevue/core';
import type { ButtonGroupPassThroughOptions } from 'primevue/buttongroup';
import { ref, computed, defineComponent } from 'vue';

/**
 * Does NOT expose `href` prop because otherwise it will conflict with the `to` prop.
 *
 * If you need to use `href`, use a `b-button` instead.
 */
export default defineComponent({
  name: 'StandardButtonGroup',
  compatConfig: {
    MODE: 3,
  },
  props: {
    disabled: {
      type: Boolean,
    },
    /**
     * Button size: 'sm', 'lg' and '' for medium
     */
    size: {
      type: String,
    },
    dataTestid: {
      type: String,
    },
  },
  setup(props) {
    const buttonRef = ref<{ $el: HTMLElement } | null>(null);

    const isButtonBusyOrDisabled = computed(() => props.disabled);

    const getSizeClass = (size: string) => {
      if (size === 'sm') return '-sm';

      if (size === 'lg') return '-lg';

      return ''; // Default to medium
    };

    /**
     * Build PassThrough props for PrimeVue unstyled mode.
     * Applies Bootstrap button classes to match BootstrapVue styling.
     *
     * Details: https://primevue.org/passthrough/
     */
    const passThroughProps = computed((): PassThrough<ButtonGroupPassThroughOptions> => {
      return {
        root: {
          class: `btn-group${props.size ? getSizeClass(props.size) : ''}`,
        },
      };
    });

    const focus = () => {
      buttonRef.value?.$el.focus();
    };

    return {
      buttonRef,
      isButtonBusyOrDisabled,
      passThroughProps,
      focus,
    };
  },
});
</script>
